import { Alignment, Fit, Layout, type Rive } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { type CSSProperties, memo, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { RIVE_CONTAINER_DEFAULT_ERROR } from '~/constants/testIDs';
import { useRiveWithStateMachines } from '~/hooks/useRiveWithStateMachines';

export type RiveContainerProps = {
  src: string;
  className?: string;
  style?: CSSProperties;
  errorFallback?: ReactNode;
  onRiveReady?: (rive: Rive) => void;
  onLoadError?: () => void;
  resizeDependencies?: number[];
  'data-testid'?: string;
};

export const RiveContainer = memo(
  ({
    src,
    className = 'size-full',
    style,
    errorFallback,
    onRiveReady,
    onLoadError,
    resizeDependencies = [],
    'data-testid': dataTestId,
  }: RiveContainerProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Create layout inside component to avoid SSR issues
    const riveLayout = useMemo(
      () =>
        new Layout({
          fit: Fit.Contain, // Like object-fit: contain
          alignment: Alignment.Center,
        }),
      [],
    );

    // Use the custom hook for Rive with state machine detection
    const { RiveComponent, rive, setContainerRef, hasError } = useRiveWithStateMachines({
      src,
      autoplay: true,
      layout: riveLayout,
      onLoadError,
    });

    // Set container ref initially
    useEffect(() => {
      if (containerRef.current) {
        setContainerRef(containerRef.current);
      }
    }, [setContainerRef]);

    // Use ResizeObserver for consistent container size detection
    useEffect(() => {
      if (!containerRef.current || !rive) return;

      const resizeObserver = new ResizeObserver(() => {
        if (rive) {
          // Force immediate resize when container size changes
          rive.resizeDrawingSurfaceToCanvas();
        }
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [rive]);

    // Additional trigger for gallery dependency changes
    useEffect(() => {
      if (!rive || resizeDependencies.length === 0) return;

      // Trigger resize when dependencies change
      rive.resizeDrawingSurfaceToCanvas();
    }, [rive, resizeDependencies]);

    // Notify parent when Rive instance is ready
    useEffect(() => {
      if (rive && onRiveReady) {
        onRiveReady(rive);
      }
    }, [rive, onRiveReady]);

    if (hasError) {
      return (
        <div ref={containerRef} className="flex items-center justify-center bg-grey-3 text-grey-8" style={style}>
          {errorFallback || (
            <span data-testid={RIVE_CONTAINER_DEFAULT_ERROR} className="text-12">
              Error loading animation
            </span>
          )}
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        className={classNames(className, 'relative overflow-hidden')}
        style={style}
        data-testid={dataTestId}
      >
        <RiveComponent />
      </div>
    );
  },
);

RiveContainer.displayName = 'RiveContainer';
